import React, { useState } from 'react';
import { Box, MenuItem, Select, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';

import CommonSegmentedControl from '../../../../form-components/CommonSegmentedControl';
import { CARD_COMPANIES, INSTALLMENT_OPTIONS } from '../../../manualSales/payment/cardPaymentForm';
import { BANK_LIST } from '../../../manualSales/payment/bankPaymentForm';

// 라인의 결제수단은 백엔드 PaymentMethod enum(CARD/CASH/TRANSFER) 값을 그대로 보낸다.
// 주문 레벨의 '카드/현금/계좌이체' 한글 값과 달리 라인은 enum 이므로 섞지 않는다.
const LINE_METHODS = [
  { value: 'CARD', label: '카드' },
  { value: 'CASH', label: '현금' },
  { value: 'TRANSFER', label: '계좌이체' },
];

// 주문 레벨 결제수단 컬럼은 여전히 한글 값을 쓰므로 라인 enum → 한글 라벨 변환이 필요하다.
export const SPLIT_METHOD_LABEL = LINE_METHODS.reduce((acc, m) => ({ ...acc, [m.value]: m.label }), {});

const EMPTY_DRAFT = {
  method: 'CARD',
  amount: '',
  installmentMonth: '00',
  cardCompanyCode: '',
  cardNumber: '',
  cid: '',
  bankInfo: '',
  depositorName: '',
};

// plain — 이미 테두리 카드(ContentSection) 안에 들어갈 때 이중 박스를 피한다(결제 정보 입력 모달).
const Wrapper = styled(Box, { shouldForwardProp: prop => prop !== 'plain' })(({ plain }) => ({
  border: plain ? 'none' : '1px solid #E5E5E5',
  borderRadius: plain ? 0 : '8px',
  padding: plain ? 0 : '16px',
}));

const FieldRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '12px',
});

const FieldCol = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
});

const FieldLabel = styled('span')({
  fontSize: '13px',
  fontWeight: 700,
  color: '#262626',
});

// 세그먼트(카드/현금/계좌이체) 바로 위에 붙는 섹션 라벨 — 피그마와 동일하게 굵게.
const MethodTitle = styled('div')({
  fontSize: '13px',
  fontWeight: 700,
  color: '#262626',
  margin: '12px 0 6px',
});

// 입력칸 옆에 붙는 보조 버튼 — TextField(40px)와 높이·라인을 맞춘다(AddButton 을 쓰면 12px 밀린다).
const FillButton = styled('button')({
  width: '100%',
  height: '40px',
  borderRadius: '6px',
  border: '1px solid #262626',
  backgroundColor: '#fff',
  color: '#262626',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#F5F5F5' },
  '&:disabled': { borderColor: '#D9D9D9', color: '#BFBFBF', cursor: 'not-allowed', backgroundColor: '#fff' },
});

const AddButton = styled('button')({
  width: '100%',
  marginTop: '12px',
  padding: '11px',
  borderRadius: '6px',
  border: '1px solid #262626',
  backgroundColor: '#fff',
  color: '#262626',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  '&:hover': { backgroundColor: '#F5F5F5' },
  '&:disabled': { borderColor: '#D9D9D9', color: '#BFBFBF', cursor: 'not-allowed', backgroundColor: '#fff' },
  // 다음 행동 가이드 — 추가할 금액이 준비되면 펄스로 시선을 끈다
  '@keyframes splitGuidePulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(74, 179, 188, 0.55)' },
    '70%': { boxShadow: '0 0 0 9px rgba(74, 179, 188, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(74, 179, 188, 0)' },
  },
  '&.split-guide-pulse': {
    borderColor: '#0F7F85',
    color: '#0F7F85',
    animation: 'splitGuidePulse 1.2s ease-out infinite',
  },
});

const LineTable = styled('table')({
  width: '100%',
  marginTop: '16px',
  borderCollapse: 'collapse',
  fontSize: '13px',
  tableLayout: 'fixed',
});

const Th = styled('th')({
  backgroundColor: '#FAFAFA',
  border: '1px solid #E5E5E5',
  padding: '8px 6px',
  fontWeight: 500,
  color: '#666',
  textAlign: 'center',
});

const Td = styled('td')({
  border: '1px solid #E5E5E5',
  padding: '8px 6px',
  textAlign: 'center',
  color: '#111',
  wordBreak: 'break-all',
  overflow: 'hidden',
  maxWidth: 0, // table-layout: fixed 에서 셀 폭을 열 폭에 고정해 긴 텍스트가 칸을 늘리지 못하게
});

const EditLineButton = styled('button')({
  width: '24px',
  height: '24px',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  border: '1px solid #BFE3E6',
  backgroundColor: '#fff',
  color: '#4AB3BC',
  cursor: 'pointer',
  marginRight: '4px',
  verticalAlign: 'middle',
});

const RemoveButton = styled('button')({
  width: '24px',
  height: '24px',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  border: '1px solid #F5A6A6',
  backgroundColor: '#fff',
  color: '#E05B5B',
  cursor: 'pointer',
  verticalAlign: 'middle',
});

const ProgressTrack = styled(Box)({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: '#E9E9E9',
  overflow: 'hidden',
  marginTop: '16px',
});

const ProgressFill = styled(Box)({
  height: '100%',
  backgroundColor: '#00B4A0',
  transition: 'width 0.2s',
});

const ProgressText = styled(Box)({
  marginTop: '8px',
  textAlign: 'center',
  fontSize: '13px',
  color: '#666',
});

const RemainAccent = styled('span')({
  color: '#00B4A0',
  fontWeight: 700,
});

const inputSx = { '& .MuiOutlinedInput-root': { height: '40px', fontSize: '13px' } };
const selectSx = { height: '40px', fontSize: '13px' };

const toNumber = value => Number(String(value ?? '').replace(/[^\d]/g, '')) || 0;

// 전화번호 가운데 가림(010-1234-5678 → 010-****-5678). 사업자번호는 가운데 2자리.
// 숫자가 아니거나 너무 짧으면(잘못 입력) 가리지 않고 그대로 보여준다 — 서버 payDetail 과 같은 규칙.
const maskDigits = (value, isBusiness) => {
  const raw = String(value || '').trim();
  const compact = raw.replace(/-/g, '');
  if (!raw) return '';
  if (!/^\d+$/.test(compact)) return raw;
  if (isBusiness) return compact.length >= 10 ? `${compact.slice(0, 3)}-**-${compact.slice(5)}` : raw;
  if (compact.length < 8) return raw;
  return `${compact.slice(0, 3)}-${'*'.repeat(compact.length - 7)}-${compact.slice(-4)}`;
};

// 라인 표의 '결제상세' 칸 — 카드는 카드사(뒤4자리)+승인번호, 이체는 은행+입금자.
// 현금·계좌이체는 주문 레벨 현금영수증 정보(cashReceipt)를 받아 승인번호를 위주로, 전화번호는 가려서 보여준다.
const describeLine = (line, cashReceipt) => {
  if (line.method === 'CARD') {
    const company = CARD_COMPANIES.find(c => c.value === line.cardCompanyCode)?.label;
    const head = [company, line.cardNumber && `(${line.cardNumber})`].filter(Boolean).join('');
    const approval = line.cid ? `승인번호: ${line.cid}` : '';
    return [head, approval].filter(Boolean);
  }
  if (line.method === 'TRANSFER' || line.method === 'CASH') {
    const parts = [];
    if (line.method === 'TRANSFER') {
      const bank = BANK_LIST.find(b => b.value === line.bankInfo)?.label || line.bankInfo;
      const head = [bank, line.depositorName].filter(Boolean).join(' ');
      if (head) parts.push(head);
    }
    const approval = cashReceipt?.approvalNumber || line.cid;
    if (approval) parts.push(`승인번호: ${approval}`);
    if (cashReceipt?.receiptYn === 'Y') {
      const isBusiness = cashReceipt.deductionType === 1;
      const masked = maskDigits(isBusiness ? cashReceipt.businessNumber : cashReceipt.phoneNumber, isBusiness);
      if (masked) parts.push(`${isBusiness ? '사업자번호' : '전화번호'}: ${masked}`);
    }
    return parts;
  }
  return [];
};

/**
 * 분할결제 수납 라인 편집기 (#49).
 * 수단·상세·금액을 입력해 '결제수단 추가'로 라인을 쌓고, 합계가 총 결제금액과 같아야 결제할 수 있다.
 * 같은 수단 중복(카드 2장)도 허용한다.
 */
// beforeAddButton: '결제수단 추가' 버튼 바로 위에 끼워 넣을 노드(함수면 현재 선택 수단을 인자로 받는다).
// 결제정보입력 모달이 현금영수증 폼을 이 슬롯으로 주입한다.
// refundMode: 환불 화면에서 사용 — 버튼/표 헤더 문구를 '환불수단/환불금액'으로 바꾼다(동작 동일).
// autoEditSingle: true 가 되는 순간 라인이 1건이면 연필(수정)을 누른 것과 같게 입력폼으로 끌어올린다.
// cashReceipt: 주문 레벨 현금영수증 입력 { approvalNumber, receiptYn, deductionType(0 개인/1 사업자), phoneNumber, businessNumber } — 현금·계좌이체 라인 결제상세 표시용
const SplitPaymentSection = ({ totalAmount, lines, onChange, formatInteger, plain = false, beforeAddButton, refundMode = false, guideTick = 0, autoEditSingle = false, cashReceipt = null }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const paid = lines.reduce((sum, line) => sum + line.amount, 0);
  const remaining = totalAmount - paid;
  const draftAmount = toNumber(draft.amount);
  // 0원 결제(전액 할인·포인트 전액 사용)도 수단 기록용 0원 라인 1개는 담을 수 있어야 한다.
  const zeroTotal = totalAmount === 0;
  const canAdd = zeroTotal ? lines.length === 0 && draftAmount === 0 : draftAmount > 0 && draftAmount <= remaining;

  const setDraftField = (name, value) => setDraft(prev => ({ ...prev, [name]: value }));

  const handleMethodChange = method => setDraft({ ...EMPTY_DRAFT, method, amount: draft.amount });

  const handleAdd = () => {
    if (!canAdd) return;
    onChange([...lines, { ...draft, amount: draftAmount, key: `${Date.now()}-${lines.length}` }]);
    setDraft({ ...EMPTY_DRAFT, method: draft.method });
  };

  const handleRemove = key => onChange(lines.filter(line => line.key !== key));

  // 부모(결제 완료 클릭 등)가 guideTick 을 올리면 '결제수단 추가'로 스크롤해 시선을 유도한다.
  const addButtonRef = React.useRef(null);
  React.useEffect(() => {
    if (!guideTick) return;
    addButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [guideTick]);
  // 추가할 준비(금액 입력 완료)가 되면 추가 버튼을 펄스로 안내한다. 남은 금액이 있으면 상시 안내.
  const guideAdd = remaining > 0 || (zeroTotal && lines.length === 0);

  // 수정 — 라인 값을 위 입력 폼(수단·상세·금액)에 세팅하고 목록에서 빼서, 고친 뒤 다시 추가하게 한다.
  const handleEdit = line => {
    setDraft({ ...EMPTY_DRAFT, ...line, amount: String(line.amount) });
    onChange(lines.filter(item => item.key !== line.key));
  };

  // 수정 모달의 '수정하기'로 잠금을 풀면 연필을 누른 것과 같게 동작한다.
  // 라인이 2건 이상이면 어느 줄을 고칠지 알 수 없어 그대로 두고, 한 번 끌어올린 뒤에는 다시 하지 않는다.
  const autoEditedRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoEditSingle || autoEditedRef.current || lines.length !== 1) return;
    autoEditedRef.current = true;
    handleEdit(lines[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEditSingle, lines]);

  return (
    <Wrapper plain={plain}>
      {/* 금액 먼저 입력하고 수단을 고르는 흐름 — 결제금액/잔액 채우기를 세그먼트 위에 둔다. */}
      <FieldRow sx={{ marginTop: 0, marginBottom: '12px' }}>
        <FieldCol>
          <FieldLabel>{t(refundMode ? '환불금액' : '결제금액')}</FieldLabel>
          <TextField
            size="small"
            color="info"
            sx={inputSx}
            placeholder={t(refundMode ? '환불금액을 입력해 주세요.' : '결제금액을 입력해 주세요.')}
            value={draft.amount === '' ? '' : formatInteger(toNumber(draft.amount))}
            onChange={e => setDraftField('amount', e.target.value)}
          />
        </FieldCol>
        <FieldCol>
          <FieldLabel>&nbsp;</FieldLabel>
          <FillButton
            type="button"
            onClick={() => setDraftField('amount', String(Math.max(0, remaining)))}
            disabled={zeroTotal ? lines.length > 0 : remaining <= 0}>
            {t('잔액 채우기')}
          </FillButton>
        </FieldCol>
      </FieldRow>

      <MethodTitle>{t(refundMode ? '환불수단' : '결제수단')}</MethodTitle>
      <CommonSegmentedControl options={LINE_METHODS} value={draft.method} onChange={handleMethodChange} />

      {draft.method === 'CARD' && (
        <>
          <FieldRow>
            <FieldCol>
              <FieldLabel>{t('할부기간')}</FieldLabel>
              <Select
                size="small"
                sx={selectSx}
                value={draft.installmentMonth}
                onChange={e => setDraftField('installmentMonth', e.target.value)}>
                {INSTALLMENT_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FieldCol>
            <FieldCol>
              <FieldLabel>{t('카드사')}</FieldLabel>
              <Select
                size="small"
                displayEmpty
                sx={selectSx}
                value={draft.cardCompanyCode}
                onChange={e => setDraftField('cardCompanyCode', e.target.value)}>
                <MenuItem value="" disabled>
                  {t('카드사 선택')}
                </MenuItem>
                {CARD_COMPANIES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FieldCol>
          </FieldRow>
          <FieldRow>
            <FieldCol>
              <FieldLabel>{t('카드번호')}</FieldLabel>
              <TextField
                size="small"
                color="info"
                sx={inputSx}
                placeholder={t('카드 뒷 4자리를 입력해 주세요.')}
                value={draft.cardNumber}
                onChange={e => setDraftField('cardNumber', e.target.value)}
              />
            </FieldCol>
            <FieldCol>
              <FieldLabel>{t('승인번호')}</FieldLabel>
              <TextField
                size="small"
                color="info"
                sx={inputSx}
                placeholder={t('승인번호를 입력해 주세요.')}
                value={draft.cid}
                onChange={e => setDraftField('cid', e.target.value)}
              />
            </FieldCol>
          </FieldRow>
        </>
      )}

      {draft.method === 'TRANSFER' && (
        <FieldRow>
          <FieldCol>
            <FieldLabel>{t('은행')}</FieldLabel>
            <Select
              size="small"
              displayEmpty
              sx={selectSx}
              value={draft.bankInfo}
              onChange={e => setDraftField('bankInfo', e.target.value)}>
              <MenuItem value="" disabled>
                {t('은행 선택')}
              </MenuItem>
              {BANK_LIST.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FieldCol>
          <FieldCol>
            <FieldLabel>{t('입금자명')}</FieldLabel>
            <TextField
              size="small"
              color="info"
              sx={inputSx}
              placeholder={t('입금자명을 입력해 주세요.')}
              value={draft.depositorName}
              onChange={e => setDraftField('depositorName', e.target.value)}
            />
          </FieldCol>
        </FieldRow>
      )}

      {/* 이 수단으로 얼마를 받는지 — 남은 금액을 넘길 수 없다 */}
      {typeof beforeAddButton === 'function' ? beforeAddButton(draft.method) : beforeAddButton}

      <AddButton
        type="button"
        ref={addButtonRef}
        className={guideAdd && canAdd ? 'split-guide-pulse' : undefined}
        onClick={handleAdd}
        disabled={!canAdd}>
        {t(refundMode ? '환불수단 추가' : '결제수단 추가')}
      </AddButton>

      {lines.length > 0 && (
        <LineTable>
          <thead>
            <tr>
              {/* 결제상세(승인번호·전화번호)가 제일 길어 폭을 넉넉히, 나머지는 줄임 */}
              <Th style={{ width: '17%' }}>{t(refundMode ? '환불수단' : '결제수단')}</Th>
              <Th style={{ width: '20%' }}>{t(refundMode ? '환불금액' : '결제금액')}</Th>
              <Th>{t(refundMode ? '환불상세' : '결제상세')}</Th>
              <Th style={{ width: '15%' }}>{t('변경')}</Th>
            </tr>
          </thead>
          <tbody>
            {lines.map(line => (
              <tr key={line.key}>
                <Td>{SPLIT_METHOD_LABEL[line.method]}</Td>
                <Td>
                  {formatInteger(line.amount)}
                  {t('원')}
                </Td>
                <Td>
                  {/* 승인번호·전화번호가 길어도 칸 밖으로 안 나가게 — 줄마다 말줄임, 전체는 마우스 올리면 title 로 */}
                  {describeLine(line, cashReceipt).map(text => (
                    <div
                      key={text}
                      title={text}
                      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                      {text}
                    </div>
                  ))}
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  <EditLineButton type="button" title={t('수정')} onClick={() => handleEdit(line)}>
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </EditLineButton>
                  <RemoveButton type="button" title={t('취소')} onClick={() => handleRemove(line.key)}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </RemoveButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </LineTable>
      )}

      <ProgressTrack>
        <ProgressFill style={{ width: `${totalAmount > 0 ? Math.min(100, (paid / totalAmount) * 100) : 0}%` }} />
      </ProgressTrack>
      <ProgressText>
        {remaining === 0 ? (
          <>
            {t('총')} {formatInteger(totalAmount)}
            {t('원')} <RemainAccent>{t('입력 완료')}</RemainAccent>
          </>
        ) : (
          <>
            {t('총')} {formatInteger(totalAmount)}
            {t('원 중')} <RemainAccent>{formatInteger(remaining)}원</RemainAccent> {t('남음')}
          </>
        )}
      </ProgressText>
    </Wrapper>
  );
};

export default SplitPaymentSection;
